package er.prateek.crossly.ads.ui

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.layout.positionInWindow
import androidx.compose.ui.platform.LocalDensity
import er.prateek.crossly.ads.AdPlacement
import er.prateek.crossly.ads.AdUnitIds
import er.prateek.crossly.ads.AdsConfig
import er.prateek.crossly.ads.createAdSlot
import er.prateek.crossly.ads.positionAdSlot
import er.prateek.crossly.ads.removeAdSlot

/**
 * Reserves the space in the Compose layout, then keeps a real DOM `<ins>` element parked on
 * top of it. The composable itself paints nothing -- the ad is genuinely outside the canvas,
 * which is the only way AdSense can render, be clicked, and be measured by Google.
 */
@Composable
actual fun PlatformAdBanner(
    config: AdsConfig,
    placement: AdPlacement,
    modifier: Modifier,
) {
    val density = LocalDensity.current.density
    val domId = remember(placement) { "crossly-ad-${placement.wireName}" }
    val client = AdUnitIds.webClient(config)
    val slot = AdUnitIds.webBannerSlot(config)

    DisposableEffect(domId, client, slot, config.testMode) {
        createAdSlot(domId, client, slot, config.testMode)
        // Navigating away must take the element with it; a fixed-position leftover would
        // otherwise hover over whatever screen comes next.
        onDispose { removeAdSlot(domId) }
    }

    Box(
        modifier = modifier.fillMaxSize().onGloballyPositioned { coordinates ->
            val position = coordinates.positionInWindow()
            positionAdSlot(
                domId = domId,
                // Compose reports pixels; CSS wants density-independent units.
                left = (position.x / density).toDouble(),
                top = (position.y / density).toDouble(),
                width = (coordinates.size.width / density).toDouble(),
                height = (coordinates.size.height / density).toDouble(),
            )
        },
    )
}
